import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { TrendingUp, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import api from '../services/api';
import useLang from '../i18n';
import { Card, Button, Breadcrumb, LoadingSpinner, DataTable } from '../components/common';
import AfghanDatePicker from '../components/common/AfghanDatePicker';
import { fmtNum, isoToAfghan, todayISO } from '../utils/dateUtils';

// ── Login page ─────────────────────────────────────────
export function Login() {
  const { t } = useLang();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [company, setCompany] = useState({});
  const { login } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/companies/public')
      .then(res => setCompany(res.data?.data || {}))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err?.message || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4" dir="rtl">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-8 border border-gray-100">
        <div className="text-center mb-8">
          {company.logo ? (
            <img src={company.logo} alt="logo" className="h-16 w-auto mx-auto mb-4 object-contain" />
          ) : (
            <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">{company.name || t('companyName')}</h1>
          <p className="text-gray-500 text-sm mt-1">{t('loginSubtitle')}</p>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('phEmail')}
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('password')}</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 disabled:opacity-60 text-sm mt-2 transition-colors"
          >
            {loading ? t('loading') : t('login')}
          </button>
        </form>
      </div>
    </div>
  );
}
  

// ── تراز آزمایشی ─────────────────────────────────────────
export function TrialBalance() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { branchId } = require('../hooks/useBranch').useBranch();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState(todayISO());
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery(
    ['trial-balance', startDate, endDate, branchId],
    () => api.get('/reports/trial-balance', { params: { startDate, endDate } }),
    { enabled: true }
  );
  const result = data?.data || {};
  const rows = result.rows || [];

  const goToLedger = (r) => navigate(`/accounting?tab=journals&accountId=${r._id}&accountLabel=${encodeURIComponent(`${r.code} — ${r.name}`)}`);

  const COLUMNS = [
    { key: 'code', label: t('code'), render: (v) => <span className="text-sm font-mono">{v}</span> },
    { key: 'name', label: t('accountName'), render: (v) => <span className="text-sm font-semibold text-blue-700">{v}</span> },
    { key: 'type', label: t('type'), render: (v) => <span className="text-xs text-gray-500">{v}</span> },
    { key: 'totalDebit', label: t('debit'), render: (v) => <span className="text-sm text-red-600 font-semibold">AFN {fmtNum(Math.round(v || 0))}</span> },
    { key: 'totalCredit', label: t('credit'), render: (v) => <span className="text-sm text-green-600 font-semibold">AFN {fmtNum(Math.round(v || 0))}</span> },
    { key: 'balance', label: t('balance'), render: (v) => <span className="text-sm font-bold text-blue-700">AFN {fmtNum(Math.round(v || 0))}</span> },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('trialBalance')}</h1>

      {/* ── فیلتر بازه‌ی تاریخ (سرچ و اکسپورت داخل خود جدول است) ── */}
      <div className="flex justify-end gap-2 mb-4">
        <div className="w-36"><AfghanDatePicker value={startDate} onChange={setStartDate} placeholder={t('startDate')} /></div>
        <div className="w-36"><AfghanDatePicker value={endDate} onChange={setEndDate} placeholder={t('endDate')} /></div>
      </div>

      {isLoading ? <LoadingSpinner full /> : (
        <>
          <Card>
            <DataTable
              columns={COLUMNS}
              data={rows}
              emptyTitle={t('noData')}
              searchValue={search}
              onSearchChange={setSearch}
              exportTitle={t('trialBalance')}
              onRowClick={goToLedger}
            />
          </Card>

          {/* ── خلاصه‌ی جمع کل ─────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <Card className="p-4 bg-red-50 border-red-100">
              <p className="text-xs text-gray-500 mb-1">{t('debit')}</p>
              <p className="text-lg font-bold text-red-700">AFN {fmtNum(Math.round(result.totalDebit || 0))}</p>
            </Card>
            <Card className="p-4 bg-green-50 border-green-100">
              <p className="text-xs text-gray-500 mb-1">{t('credit')}</p>
              <p className="text-lg font-bold text-green-700">AFN {fmtNum(Math.round(result.totalCredit || 0))}</p>
            </Card>
            <Card className={`p-4 ${result.balanced ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
              <p className="text-xs text-gray-500 mb-1">{t('status')}</p>
              <p className={`text-lg font-bold ${result.balanced ? 'text-green-700' : 'text-red-700'}`}>
                {result.balanced ? `✓ ${t('balanced')}` : `✗ ${t('unbalanced')}`}
              </p>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}